import React from 'react';
import Button from '../../../components/ui/button';

/**
 * Props for the EditActionBar component
 */
export interface EditActionBarProps {
  /** Current editing mode */
  mode: 'view' | 'edit';
  
  /** Whether the journey is awaiting approval after AI processing */
  awaitingApproval: boolean;
  
  /** Whether a submission is in progress */
  isSubmitting: boolean;
  
  /** Number of pending changes */
  pendingChangesCount: number;
  
  /** Whether the journey has a backend ID (saved or original) */
  hasOriginalJourneyId: boolean;
  
  /** Handler to submit changes for AI processing */
  onSubmitChanges: () => void;
  
  /** Handler to discard all pending changes */
  onDiscardChanges: () => void;
  
  /** Handler to approve and update the existing journey */
  onApproveUpdate: () => void;
  
  /** Handler to approve and create a new journey */
  onApproveCreateNew: () => void;
  
  /** Handler to approve and save an unsaved journey */
  onApproveCreateUnsaved: () => void;
}

/**
 * EditActionBar - Displays action buttons for journey editing
 * 
 * This component provides a sticky action bar at the bottom of the journey editor
 * with context-appropriate buttons based on the current editing state:
 * - Edit mode: Submit and Discard buttons
 * - Approval mode: Approve and Discard buttons (with different approval options)
 *
 * View mode controls have been removed to keep the UI focused on edit/review flows.
 */
export const EditActionBar: React.FC<EditActionBarProps> = ({
  mode,
  awaitingApproval,
  isSubmitting,
  pendingChangesCount,
  hasOriginalJourneyId,
  onSubmitChanges,
  onDiscardChanges,
  onApproveUpdate,
  onApproveCreateNew,
  onApproveCreateUnsaved,
}) => {
  // Hide the action bar entirely in pure view mode
  if (mode === 'view' && !awaitingApproval) {
    return null;
  }

  return (
    <div
      className="sticky bottom-0 z-50 border border-orange-200 rounded-md shadow-lg flex flex-col items-center gap-1"
      style={{ background: "rgba(255, 247, 237, 0.98)", padding: "0.5rem 1rem" }}
    >
      {/* Edit Mode Controls */}
      <div className="flex flex-col sm:flex-row gap-1 w-full justify-center">
        {awaitingApproval ? (
          // Approval Mode: Different buttons based on whether journey has an ID
          <>
            {hasOriginalJourneyId ? (
              // Journey has ID: Offer update existing or create new
              <>
                <Button
                  variant="primary"
                  onClick={onApproveUpdate}
                  disabled={isSubmitting}
                  className="px-3 py-1.5"
                >
                  {isSubmitting ? '⏳ Saving...' : '✅ Update existing journey draft'}
                </Button>
                <Button
                  variant="secondary"
                  onClick={onApproveCreateNew}
                  disabled={isSubmitting}
                  className="px-3 py-1.5"
                >
                  📝 Save as new journey draft
                </Button>
              </>
            ) : (
              // Journey has no ID: Only offer to save
              <Button
                variant="primary"
                onClick={onApproveCreateUnsaved}
                disabled={isSubmitting}
                className="px-3 py-1.5"
              >
                {isSubmitting ? '⏳ Saving...' : '💾 Save journey draft'}
              </Button>
            )}
            <Button
              variant="secondary"
              onClick={onDiscardChanges}
              disabled={isSubmitting}
              className="px-3 py-1.5"
            >
              ❌ Discard Changes
            </Button>
          </>
        ) : (
          // Edit Mode: Submit and Discard buttons
          <>
            <Button
              variant="primary"
              onClick={onSubmitChanges}
              disabled={isSubmitting || pendingChangesCount === 0}
              className="px-3 py-1.5"
            >
              {isSubmitting ? '⏳ Processing...' : '✨ Submit Changes'}
            </Button>
            <Button
              variant="secondary"
              onClick={onDiscardChanges}
              disabled={isSubmitting}
              className="px-3 py-1.5"
            >
              ❌ Discard Changes
            </Button>
          </>
        )}
      </div>

      {/* Pending Changes Counter and Hint - Only shown in edit mode */}
      {mode === 'edit' && !awaitingApproval && (
        <span className="flex flex-row gap-3 items-center text-sm text-[var(--text-muted)] font-normal mt-1">
          {pendingChangesCount > 0 && (
            <span>
              {pendingChangesCount} pending change{pendingChangesCount !== 1 ? 's' : ''}
            </span>
          )}
          <span>
            • Submit your changes to see the updated journey.
          </span>
        </span>
      )}

      {/* Warning about recalculated values */}
      <span className="flex items-center gap-1 text-sm text-[var(--text)] font-normal mt-1">
        <span>* Values marked with asterisk will be recalculated when you save changes</span>
      </span>
    </div>
  );
};
